import React, { useState, useCallback } from "react";
import Papa from "papaparse";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Download, Upload, FileSpreadsheet, Check, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";

const stripBOM = (s) => s.replace(/^\uFEFF/, "");

function parseCsv(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = stripBOM(reader.result);
      Papa.parse(text, {
        delimiter: ";", header: true, skipEmptyLines: true, quoteChar: '"',
        complete: (res) => resolve({ rows: res.data, fields: res.meta.fields || [] }),
        error: reject,
      });
    };
    reader.onerror = reject;
    reader.readAsText(file, "utf-8");
  });
}

export function ProductImportExport({ onImported }) {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [parsed, setParsed] = useState(null); // {rows, fields, name}
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("aanvullen");

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile("/producten/export.csv", `producten-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Export gedownload");
    } catch {
      toast.error("Export mislukt");
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadFile("/producten/import-template.csv", "producten-sjabloon.csv");
      toast.success("Sjabloon gedownload");
    } catch {
      toast.error("Sjabloon downloaden mislukt");
    }
  };

  const onPick = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { rows, fields } = await parseCsv(file);
      setParsed({ rows, fields, name: file.name });
      setResult(null);
      setOpen(true);
    } catch {
      toast.error("Kon CSV niet lezen");
    }
  }, []);

  const doImport = async () => {
    if (!parsed) return;
    setImporting(true);
    try {
      const { data } = await api.post("/producten/import", { rows: parsed.rows, mode });
      setResult(data);
      toast.success(`${data.nieuw} nieuw · ${data.bijgewerkt} bijgewerkt`);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import mislukt");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="product-export-btn">
        <Download className="h-4 w-4 mr-1" /> {exporting ? "Bezig…" : "Exporteer CSV"}
      </Button>

      {isAdmin && (
        <>
          <Button variant="outline" onClick={downloadTemplate} data-testid="product-template-btn">
            <FileDown className="h-4 w-4 mr-1" /> Sjabloon (CSV)
          </Button>
          <input id="product-import-input" type="file" accept=".csv,text/csv"
            className="hidden" onChange={onPick} data-testid="product-import-input" />
          <Button variant="outline" onClick={() => document.getElementById("product-import-input")?.click()}
            data-testid="product-import-btn">
            <Upload className="h-4 w-4 mr-1" /> Importeer CSV
          </Button>
        </>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setParsed(null); setResult(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="product-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" /> Producten importeren
            </DialogTitle>
          </DialogHeader>

          {parsed && !result && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium truncate">{parsed.name}</span>
                <Badge variant="outline">{parsed.rows.length} rij(en)</Badge>
              </div>
              <p className="text-muted-foreground">
                Matchen op <code className="px-1 bg-secondary rounded">sku</code>. Verplichte kolommen: <strong>sku</strong> en <strong>naam</strong>.
              </p>

              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Importmodus</div>
                {[
                  { id: "aanvullen", label: "Aanvullen (veilig)", desc: "Bestaande producten bijwerken, maar alleen de kolommen die in je CSV staan. Lege/ontbrekende kolommen blijven ongewijzigd." },
                  { id: "toevoegen", label: "Alleen toevoegen", desc: "Alleen nieuwe sku's importeren; bestaande producten worden overgeslagen." },
                  { id: "overschrijven", label: "Overschrijven", desc: "Bestaande producten volledig vervangen. Let op: kolommen die niet in de CSV staan worden op leeg/0 gezet." },
                ].map((m) => (
                  <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`import-mode-${m.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${mode === m.id ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${mode === m.id ? "border-primary bg-primary" : "border-muted-foreground"}`} />
                      <span className="font-medium text-sm">{m.label}</span>
                      {m.id === "aanvullen" && <Badge variant="outline" className="text-[10px]">aanbevolen</Badge>}
                      {m.id === "overschrijven" && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">let op</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 ml-5">{m.desc}</div>
                  </button>
                ))}
              </div>
              {!parsed.fields.includes("sku") && (
                <div className="flex items-start gap-2 p-2 bg-destructive/10 border border-destructive/30 rounded text-destructive">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Kolom <strong>sku</strong> ontbreekt. Gebruik het export-bestand als sjabloon.</span>
                </div>
              )}
              <div className="border border-border rounded-lg overflow-x-auto max-h-60">
                <table className="text-xs w-full">
                  <thead className="bg-secondary sticky top-0">
                    <tr>{parsed.fields.slice(0, 6).map((c) => <th key={c} className="text-left px-2 py-1.5 font-medium">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 8).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        {parsed.fields.slice(0, 6).map((c) => (
                          <td key={c} className="px-2 py-1 max-w-[140px] truncate">{String(r[c] ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-2 text-sm" data-testid="import-result">
              <div className="flex items-center gap-2 text-primary font-medium">
                <Check className="h-4 w-4" /> Import voltooid
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Totaal", result.totaal], ["Nieuw", result.nieuw], ["Bijgewerkt", result.bijgewerkt], ["Overgeslagen", result.overgeslagen]].map(([l, v]) => (
                  <div key={l} className="bg-secondary/50 rounded-lg p-2">
                    <div className="text-lg font-display">{v}</div>
                    <div className="text-xs text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
              {result.errors?.length > 0 && (
                <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto border border-border rounded p-2">
                  {result.errors.slice(0, 20).map((e, i) => <div key={i}>Rij {e.row + 1}: {e.error}</div>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={doImport} disabled={importing || !parsed?.fields.includes("sku")} data-testid="confirm-product-import">
                  {importing ? "Bezig…" : `Importeer ${parsed?.rows.length || 0} rij(en)`}
                </Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)} data-testid="close-import">Sluiten</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
